import { useSearchParams } from 'react-router-dom';
import GuideMeTitleBar from 'src/components/GuideMeTitleBar';
import Button from 'src/components/design-system/Button';
import useReceiptDetails from 'src/hooks/useReceiptDetails';
import { errorIcon, informationIcon } from 'src/utils/icons';

const MatchItems = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      Enter delivery-related data, as required by your organization.
    </li>
    <li className="list-disc font-semibold">
      Enter or review the quantities received for each item in the delivery.
    </li>
  </ul>
);

const EditLineItems = () => {
  const { isBlindReceive, isAddLineItems } = useReceiptDetails();

  return (
    <ul className="px-4 font-bold text-[13px]">
      <li className="list-disc font-semibold">
        For products not received (i.e. rejected, discontinued, or backordered), select the line(s)
        containing the missing product(s) and click on the "Zero Qty" button to reduce the Qty
        Received to "0".
      </li>

      {isAddLineItems && (
        <>
          <li className="list-disc font-semibold">
            For products delivered that were not part of the original PO record, click
            <Button color="success" className="inline-block">
              Add Line Item
            </Button>
            button to add a blank item row. Fill in the row details, and save your changes.
          </li>
        </>
      )}

      {!isBlindReceive && (
        <li className="list-disc font-semibold">
          Look for or filter for rows displaying the
          <span className="inline-block  px-2 relative top-[6px]">{informationIcon} </span>
          icon to review discrepancies between the PO and receipt record.
        </li>
      )}
    </ul>
  );
};

const LockReceipt = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      When the receipt record accurately reflects the delivery, Click the
      <Button color="success" className="inline-block">
        Complete
      </Button>
      button to Complete.
    </li>
    <li className="list-disc font-semibold">
      <p>
        Resolve any errors identified by this icon
        <span className="inline-block  px-2 relative top-[6px]">{errorIcon} </span>
        and save your changes.
      </p>
    </li>
  </ul>
);

const ReceiptLocked = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      <div className="flex justify-start items-center flex-row gap-2 mt-1">
        Click
        <span className={`font-semibold text-[13px]`}>
          <Button color="dark">Re-Open</Button>
        </span>
        button to make changes.
      </div>
    </li>
  </ul>
);

const ReceiptDeleted = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">
      <div className="flex justify-start items-center flex-row gap-2 mt-1">
        Click
        <span className={`font-semibold text-[13px]`}>
          <Button color="dark">UnDelete</Button>
        </span>
        button to make changes.
      </div>
    </li>
  </ul>
);

const ReturnToEnterReceipt = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc  font-semibold">Save your changes before leaving the page.</li>
    <li className="list-disc font-semibold py-2 ">
      <p className="flex items-center">
        Resolve any errors identified by this icon
        <span className="px-2">{errorIcon}</span>
      </p>
    </li>
    <li className="list-disc font-semibold">
      When you are done making changes, click on 'Enter Receipt' to start entering the next invoice.
    </li>
  </ul>
);

const IndirectReceiptEntry = () => (
  <ul className="px-4 font-bold text-[13px]">
    <li className="list-disc font-semibold">Save your changes.</li>
    <li className="list-disc font-semibold">
      <p className="flex items-center">
        Resolve any errors identified by this icon
        <span className=" px-2">{errorIcon}</span>
      </p>
    </li>
  </ul>
);

export const useGuideMeSteps = (isCurrentDocLinked: boolean) => {
  const { isBlindReceive, isInProgress, isCompleted, isDeleted } = useReceiptDetails();
  const [searchParams] = useSearchParams();
  const isReceiptEntryInDirect = searchParams.get('from') !== 'enter_receipts';

  return [
    {
      content: <MatchItems />,
      title: <GuideMeTitleBar title={isBlindReceive ? 'Record Items' : 'Match Items'} />,
    },
    ...(isInProgress
      ? [
          {
            content: <EditLineItems />,
            title: <GuideMeTitleBar title="Edit Line Items" />,
          },
        ]
      : []),
    ...(isInProgress
      ? [
          {
            content: <ReturnToEnterReceipt />,
            title: <GuideMeTitleBar title="Return to Enter Receipt" />,
          },
        ]
      : []),
    ...(isInProgress
      ? [
          {
            content: <LockReceipt />,
            title: <GuideMeTitleBar title="Complete Receipt" />,
          },
        ]
      : []),

    ...(isReceiptEntryInDirect
      ? [
          {
            content: <IndirectReceiptEntry />,
            title: <GuideMeTitleBar title="Resolve Errors" />,
          },
        ]
      : []),
    ...(isCompleted && !isCurrentDocLinked
      ? [
          {
            content: <ReceiptLocked />,
            title: <GuideMeTitleBar title="Receipt Completed" />,
          },
        ]
      : []),
    ...(isDeleted
      ? [
          {
            content: <ReceiptDeleted />,
            title: <GuideMeTitleBar title="Receipt Deleted" />,
          },
        ]
      : []),
  ];
};

export default useGuideMeSteps;
